import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Play, RefreshCw } from 'lucide-react'
import { Button } from '@quebon/ui/components/button'

interface Props {
  url: string
  label: string
  posterUrl?: string
  /** Fetch a fresh authorized URL after expiry or a media error. */
  onRefreshUrl?: () => Promise<string | undefined>
}

/** A click starts one video. Lists do not fetch every video's metadata or original file. */
export function LcEvidenceVideoPlayer(props: Props) {
  return <VideoResource key={props.url} {...props} />
}

function VideoResource({ url, label, posterUrl, onRefreshUrl }: Props) {
  const { t } = useTranslation('common')
  const [active, setActive] = useState(false)
  const [playbackUrl, setPlaybackUrl] = useState(url)
  const [revision, setRevision] = useState(0)
  const [failed, setFailed] = useState(false)
  const [refreshing, setRefreshing] = useState(false)

  const retry = async () => {
    if (refreshing) return
    setRefreshing(true)
    try {
      const freshUrl = onRefreshUrl ? await onRefreshUrl() : playbackUrl
      if (!freshUrl) return
      setPlaybackUrl(freshUrl)
      setRevision(value => value + 1)
      setFailed(false)
    } catch {
      setFailed(true)
    } finally {
      setRefreshing(false)
    }
  }

  return (
    <div className="space-y-2" data-testid="evidence-video-player">
      <div className="overflow-hidden rounded-lg border border-border bg-muted">
        {active ? (
          <video
            key={revision}
            src={playbackUrl}
            poster={posterUrl}
            controls
            playsInline
            autoPlay
            preload="metadata"
            aria-label={label}
            className="max-h-[420px] w-full bg-black object-contain"
            onError={() => setFailed(true)}
          />
        ) : (
          <button
            type="button"
            onClick={() => setActive(true)}
            aria-label={t('evidenceVideo.play', { label })}
            className="flex min-h-36 w-full items-center justify-center gap-2 px-4 py-6 text-sm font-medium text-foreground hover:bg-accent focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
          >
            <Play className="size-5" aria-hidden="true" />
            {label}
          </button>
        )}
      </div>
      {failed ? (
        <div role="alert" className="flex flex-wrap items-center gap-2 text-sm text-destructive">
          <span>{t('evidenceVideo.error')}</span>
          <Button type="button" variant="outline" size="sm" disabled={refreshing} onClick={() => void retry()}>
            <RefreshCw className={refreshing ? 'size-4 animate-spin' : 'size-4'} aria-hidden="true" />
            {refreshing ? t('evidenceVideo.refreshing') : t('evidenceVideo.retry')}
          </Button>
        </div>
      ) : null}
    </div>
  )
}
